// ./src/hooks/useFeatureGate.ts
//
// ⭐ THE ONE PLACE THAT ANSWERS "is this feature available?" — for the account, and for a single
// upgraded event (ROADMAP #209).
//
// MONETIZATION.md §2 states the rule this file exists to keep: *"`hasFeature(code, { eventId })`
// resolves event grant OR account plan, and only that function is allowed to answer the
// question — no call site re-derives it."* Every gate in the app goes through here. A page
// that reads `hasAppPlanFeature` directly, or compares a plan number itself, is the bug this
// prevents — because it would not learn about an event's upgrade, a draft, or test mode.
//
// The order, as the server's `planFeatureGate` judges it:
//   1. the plan bought for THIS event (`resolveEventGrant` — ms_auth's `scopePlan`, read through `myEventAccess`);
//   2. the plan of the account the record belongs to (#142);
//   3. ROADMAP #231 — a DRAFT, or an event in TEST MODE, tries everything (rule 6): allowed, and marked `premium`, because
//      publishing or going live asks the plan for it.
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { arePaywallsActive, getRoutePath } from "@/config";
import { BRAND_ID } from "@/config/branding";
import { useApp } from "@/contexts/AppContext";
import { useCrewAccess } from "@/contexts/CrewAccessContext/CrewAccessContext";
import { useCurrentEvent } from "@/contexts/CurrentEventContext/CurrentEventContext";
import { boughtPlanGrants, EntitlementVerdict, evaluateEntitlement, featureDisplayName, PlanFeatureMap, planFeaturesFor } from "@/utils/planEntitlement";
import { eventUpgradeOffer, plansStagePath } from "@/utils/planOffers";
import { PLAN_TRIAL_SAMPLE, planSampleAllows, planSampleRefusal } from "@/utils/planTrial";

/** The status of an event nobody outside the crew can see yet (the API's `EVENT_STATUS.DRAFT`). */
const EVENT_STATUS_DRAFT = "draft";

export interface FeatureGateScope {
  /**
   * The event the action belongs to, when there is one. Carried through today so that call
   * sites are already correct when event-scoped grants land — see the banner above.
   */
  eventId?: string | null;
  /**
   * For a NUMERIC ceiling: how many already exist. `currentCount >= limit` refuses, so pass
   * the count BEFORE adding — the same contract the server's gate uses.
   */
  currentCount?: number | null;
  /**
   * #238 — adding to a BACK-OFFICE tool: a draft or a test run tries only a SAMPLE (`utils/planTrial.ts`) — `count` = the
   * records it has (null = not known here — a narrowed or unloaded list: the server decides), `adding` = how many this
   * makes (1 unless a batch), `publishAsks` = publishing asks for it too (volunteer shifts). The server's `sample`.
   */
  sample?: { count: number | null; adding?: number; noun: readonly [string, string]; publishAsks?: boolean };
  /** #238 — an OUTPUT (an export, a print): a draft or a test run is not open to it — reports are only viewed there. */
  output?: boolean;
}

export interface UseFeatureGateReturn {
  /**
   * The full verdict: allowed, why, the ceiling, and the exact sentence the server would
   * have returned on a refusal.
   */
  checkFeature: (code: string, scope?: FeatureGateScope) => EntitlementVerdict;
  /** The boolean shorthand. Prefer `checkFeature` when you need to explain the refusal. */
  hasFeature: (code: string, scope?: FeatureGateScope) => boolean;
  /**
   * Send the host to the page that can actually fix it: on partylabz (a free account, in an event) the event's own upgrade
   * — ROADMAP #209 — and everywhere else the subscription. `eventUpgradeOffer` decides, as it does for every upsell.
   */
  goToUpgrade: () => void;
  /**
   * #60 — `goToUpgrade` opens THIS event's plans (its upgrade), not the subscription, so a button says "Upgrade this
   * event". The same decision as `goToUpgrade`'s, so a label and the place it leads cannot disagree.
   */
  upgradesEvent: boolean;
  /** Whether gates are being ENFORCED right now (the kill switch, or the preview switch). */
  enforcing: boolean;
}

/**
 * ROADMAP #209 — does the plan bought for THIS event (its upgrade) grant the feature?
 *
 * Returns `null` = "no event-scoped answer exists" (no upgrade known for that event — the caller falls through to the
 * account plan), and `true` only for a REAL grant. ⚠ An upgrade grants only what its plan STATES, as the server's judge
 * does: a feature it does not carry, or a ceiling asked without a count, is no grant (never the account plan's
 * fail-open). It stays the ONLY reader, so the fall-through order is stated once rather than at every gate.
 */
const resolveEventGrant = (
  code: string,
  eventId: string | null | undefined,
  upgrade: { eventId: string | null; features: PlanFeatureMap } | null,
  currentCount?: number | null,
): boolean | null => {
  if (!upgrade?.features || !eventId || !upgrade.eventId || String(upgrade.eventId) !== String(eventId)) {
    return null;
  }

  return boughtPlanGrants(upgrade.features, code, currentCount) ? true : null;
};

export const useFeatureGate = (): UseFeatureGateReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appPlansFeatures, user } = useApp();
  // #142 — the event workspace's own plan (its account's), for a create that lands there; #209 — and its own upgrade.
  const { eventId: workspaceEventId, ownsEvent, planFeatures: eventPlanFeatures, eventUpgrade } = useCrewAccess();
  // #231 d — the event being worked on, for its test mode.
  const { currentEvent } = useCurrentEvent();

  const enforcing = arePaywallsActive();

  const checkFeature = useCallback(
    (code: string, scope?: FeatureGateScope): EntitlementVerdict => {
      // While the gates are off, the SERVER allows everything too — so a lock here would be
      // a refusal nothing is actually making. Answer "allowed" and carry the feature's name
      // so a call site can still render an advisory badge if it wants one.
      if (!enforcing) {
        return {
          code,
          allowed: true,
          reason: "no_plan",
          limit: null,
          featureName: featureDisplayName(code, appPlansFeatures?.[code]),
          message: null,
        };
      }

      const scopedEventId = scope?.eventId ?? workspaceEventId ?? currentEvent?.id ?? null;
      const featureName = featureDisplayName(code, appPlansFeatures?.[code]);

      // ── Event grant OR account plan, in that order (ROADMAP #209) ────────────────────
      const granted = resolveEventGrant(code, scopedEventId, eventUpgrade ? { eventId: workspaceEventId, features: eventUpgrade.features } : null, scope?.currentCount);

      if (granted === true) {
        return { code, allowed: true, reason: "event_upgrade", limit: null, featureName, message: null, premium: false };
      }

      // ⚠ The account the record will belong to decides (#142): the event's, in its workspace — never a crew member's own.
      const features = planFeaturesFor(
        appPlansFeatures,
        { eventId: workspaceEventId, ownsEvent, planFeatures: eventPlanFeatures },
        scope?.eventId,
      );
      const verdict = evaluateEntitlement(features, code, scope?.currentCount);

      if (verdict.allowed) {
        return { ...verdict, premium: false };
      }

      // ── ROADMAP #231 — a DRAFT, or an event in TEST MODE, tries everything (the server's rule 6) ──
      // Allowed, and marked premium: publishing (or going live) asks the plan for it.
      const open = !!currentEvent && !!scopedEventId && String(scopedEventId) === String(currentEvent.id) && (currentEvent.isTestMode || currentEvent.eventStatus === EVENT_STATUS_DRAFT);

      // #238 — not an output (an export or a print is never opened by a draft), and a back-office tool only its sample.
      if (open && !scope?.output) {
        const sample = scope?.sample
          ? { count: scope.sample.count, limit: PLAN_TRIAL_SAMPLE, noun: scope.sample.noun, publishAsks: scope.sample.publishAsks === true }
          : undefined;

        if (!scope?.sample || scope.sample.count == null || planSampleAllows(scope.sample.count, scope.sample.adding)) {
          return { ...verdict, allowed: true, reason: currentEvent!.isTestMode ? "test_mode" : "draft", message: null, premium: true, sample };
        }

        return { ...verdict, message: planSampleRefusal(featureName, scope.sample.noun), premium: true, sample };
      }

      return { ...verdict, premium: true };
    },
    [appPlansFeatures, enforcing, workspaceEventId, ownsEvent, eventPlanFeatures, currentEvent, eventUpgrade]
  );

  const hasFeature = useCallback(
    (code: string, scope?: FeatureGateScope): boolean => {
      return checkFeature(code, scope).allowed;
    },
    [checkFeature]
  );

  const upgradesEvent = !!workspaceEventId && eventUpgradeOffer(user?.account?.currentPlan, BRAND_ID) === "offer";

  const goToUpgrade = useCallback(() => {
    if (upgradesEvent && workspaceEventId) {
      // #60 — the plans are a stage of the event's dashboard; `back` returns the host to the page that refused them when
      // the drawer closes (after paying, or not). The dashboard accepts only a page of this same event.
      navigate(plansStagePath(workspaceEventId, `${location.pathname}${location.search}`));

      return;
    }

    navigate(getRoutePath("subscription"));
  }, [navigate, upgradesEvent, workspaceEventId, location.pathname, location.search]);

  return useMemo(
    () => ({ checkFeature, hasFeature, goToUpgrade, upgradesEvent, enforcing }),
    [checkFeature, hasFeature, goToUpgrade, upgradesEvent, enforcing]
  );
};
